/**
 * ProjectsListView — `/projects`
 *
 * Lista de proyectos del tenant alineada al prototipo (Pantalla 5):
 *  - Header con CTA "Nuevo proyecto"
 *  - Filtros: status + methodology
 *  - Tabla: Proyecto · Cliente · Metodología · Status · Created
 *  - Click en fila → /projects/$id/overview
 *
 * El wizard de creación se monta como modal en B.5.e.2 (por ahora el
 * botón "Nuevo proyecto" queda disabled con tooltip).
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Filter, Plus, Trash2 } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  listProjects,
  type Project,
  type ProjectMethodology,
  type ProjectStatus,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { canCreateProject, canManageTenant } from '../../lib/tiers'
import { isSupportedLocale, toIntlLocale } from '../../i18n/config'
import { DeleteProjectDialog } from '../../components/projects/DeleteProjectDialog'
import { MethodologyBadge } from '../../components/projects/MethodologyBadge'
import { ProjectStatusBadge } from '../../components/projects/ProjectStatusBadge'
import { ProjectWizardModal } from '../../components/projects/wizard/ProjectWizardModal'
import { ScrollPage } from '../../components/shell/ScrollPage'
import i18n from 'i18next'

const STATUS_OPTIONS: ProjectStatus[] = [
  'draft',
  'active',
  'on_hold',
  'closed',
  'cancelled',
]
const METHODOLOGY_OPTIONS: ProjectMethodology[] = [
  'waterfall',
  'scrum',
  'kanban',
  'hybrid',
]

export function ProjectsListView() {
  const { t } = useTranslation(['projects', 'common', 'errors'])
  const { me } = useMe()
  const { accessToken, loading: authLoading } = useAccessToken()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Filtros persisten en URL para que el usuario pueda volver del
  // detalle de un proyecto y conservar el contexto (status / methodology
  // seleccionados). Mismo patrón que el catálogo de plantillas.
  const navigateFilters = useNavigate({ from: '/projects' })
  const searchParams = useSearch({ from: '/projects' })
  const statusFilter: ProjectStatus | '' = searchParams.status ?? ''
  const methodologyFilter: ProjectMethodology | '' =
    searchParams.methodology ?? ''
  const setStatusFilter = (next: ProjectStatus | '') =>
    void navigateFilters({
      search: (prev) => {
        const out = { ...prev }
        if (next === '') delete out.status
        else out.status = next
        return out
      },
      replace: true,
    })
  const setMethodologyFilter = (next: ProjectMethodology | '') =>
    void navigateFilters({
      search: (prev) => {
        const out = { ...prev }
        if (next === '') delete out.methodology
        else out.methodology = next
        return out
      },
      replace: true,
    })

  const [wizardOpen, setWizardOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)

  const query = useQuery({
    queryKey: ['projects', 'list', statusFilter || 'all'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjects(
        accessToken,
        statusFilter ? { project_status: statusFilter } : undefined,
      )
    },
    enabled: !!accessToken,
  })

  const all = useMemo(() => query.data ?? [], [query.data])
  const filtered = useMemo(() => {
    if (!methodologyFilter) return all
    return all.filter((p) => p.methodology === methodologyFilter)
  }, [all, methodologyFilter])

  // OT-0009 §1.2 #10/#11 — "Nuevo proyecto" y "Eliminar" no miraban
  // tier: un reviewer veía ambos y el backend los rechazaba al enviar.
  const canCreate = canCreateProject(me?.role, me?.tenant?.access)
  const canDelete = canManageTenant(me?.role, me?.tenant?.access)

  const tenantName = me?.tenant?.display_name ?? '—'
  const total = all.length
  const subtitle =
    total === 0
      ? t('list.subtitle_zero', { tenantName })
      : t('list.subtitle', { total, tenantName })

  return (
    <ScrollPage>
      <div className="max-w-7xl">
      {/* Header */}
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-ink">{t('list.title')}</h1>
          <p className="mt-1 font-body text-sm text-ink-muted">{subtitle}</p>
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 font-ui text-sm text-canvas hover:bg-brand-deep transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('list.new_project')}
          </button>
        ) : null}
      </header>

      {/* El asistente consulta el catálogo de roles al montarse, abierto o no.
          Solo se monta para quien puede crear proyectos: sin esto, el
          `external` pedía ese catálogo en cada visita a `/projects` y recibía
          `403 FORBIDDEN_TIER` sin que nada lo mostrara (OT-0015, H-7). */}
      {canCreate ? (
        <ProjectWizardModal
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={(projectId) => {
            // Invalida la lista y manda al overview del proyecto recién creado.
            void queryClient.invalidateQueries({ queryKey: ['projects', 'list'] })
            setWizardOpen(false)
            void navigate({
              to: '/projects/$projectId/overview',
              params: { projectId },
            })
          }}
        />
      ) : null}

      {/* Filtros */}
      <div className="bg-surface border border-line rounded-md p-3 mb-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <Filter className="w-3.5 h-3.5" />
          {t('list.filters.label')}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | '')}
          className="bg-canvas border border-line rounded-md px-2.5 py-1.5 text-xs"
        >
          <option value="">
            {t('list.filters.status')}: {t('list.filters.all')}
          </option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}` as const)}
            </option>
          ))}
        </select>
        <select
          value={methodologyFilter}
          onChange={(e) =>
            setMethodologyFilter(e.target.value as ProjectMethodology | '')
          }
          className="bg-canvas border border-line rounded-md px-2.5 py-1.5 text-xs"
        >
          <option value="">
            {t('list.filters.methodology')}: {t('list.filters.all')}
          </option>
          {METHODOLOGY_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {t(`methodology.${m}` as const)}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-ink-subtle">
          {t('list.filters.showing', { shown: filtered.length, total })}
        </span>
      </div>

      {/* Body */}
      {authLoading || query.isLoading ? (
        <p className="font-mono text-xs text-ink-subtle">{t('list.loading')}</p>
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : total === 0 ? (
        <EmptyState canCreate={canCreate} />
      ) : (
        <ProjectsTable
          projects={filtered}
          canDelete={canDelete}
          onRequestDelete={setProjectToDelete}
        />
      )}

      <DeleteProjectDialog
        project={projectToDelete}
        open={projectToDelete !== null}
        onClose={() => setProjectToDelete(null)}
      />
      </div>
    </ScrollPage>
  )
}

interface ProjectsTableProps {
  projects: Project[]
  canDelete: boolean
  onRequestDelete: (project: Project) => void
}

function ProjectsTable({ projects, canDelete, onRequestDelete }: ProjectsTableProps) {
  const { t } = useTranslation('projects')
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted text-ink-muted text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left font-medium px-5 py-3">
              {t('list.table.project')}
            </th>
            <th className="text-left font-medium px-3 py-3">
              {t('list.table.client')}
            </th>
            <th className="text-left font-medium px-3 py-3">
              {t('list.table.methodology')}
            </th>
            <th className="text-left font-medium px-3 py-3">
              {t('list.table.status')}
            </th>
            <th className="text-left font-medium px-3 py-3">
              {t('list.table.created')}
            </th>
            <th className="px-3 py-3 w-12" aria-label="actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {projects.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              canDelete={canDelete}
              onRequestDelete={() => onRequestDelete(p)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ProjectRow({
  project,
  canDelete,
  onRequestDelete,
}: {
  project: Project
  canDelete: boolean
  onRequestDelete: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <tr className="hover:bg-surface-muted group">
      <td className="px-5 py-3">
        <Link
          to="/projects/$projectId/overview"
          params={{ projectId: project.id }}
          className="block"
        >
          <div className="font-medium text-ink">{project.name}</div>
          <div className="text-[10px] font-mono text-ink-subtle">
            {project.code}
          </div>
        </Link>
      </td>
      <td className="px-3 py-3 text-ink-muted">{project.client_name ?? '—'}</td>
      <td className="px-3 py-3">
        <MethodologyBadge methodology={project.methodology} />
      </td>
      <td className="px-3 py-3">
        <ProjectStatusBadge status={project.status} />
      </td>
      <td className="px-3 py-3 text-ink-muted text-xs">
        {formatDate(project.created_at)}
      </td>
      <td className="px-3 py-3 text-right">
        {canDelete ? (
          <button
            type="button"
            onClick={onRequestDelete}
            aria-label={t('list.row_actions.delete_aria', { name: project.name })}
            title={t('list.row_actions.delete')}
            className="p-1.5 rounded-md text-ink-subtle hover:text-critical hover:bg-critical-soft opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ) : null}
      </td>
    </tr>
  )
}

/** Sólo invita a crear a quien puede crear: un revisor, o cualquiera con la
 *  organización en solo lectura (OT-0073 §4.4), no ve "Nuevo proyecto". */
function EmptyState({ canCreate }: { canCreate: boolean }) {
  const { t } = useTranslation('projects')
  return (
    <div className="rounded-md border border-dashed border-line bg-surface p-10 text-center">
      <h3 className="font-display text-lg text-ink">{t('list.empty_title')}</h3>
      <p className="mt-2 max-w-prose mx-auto font-body text-sm text-ink-muted">
        {canCreate ? t('list.empty_body') : t('list.empty_body_no_create')}
      </p>
    </div>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as const, { defaultValue: fallback })}
    </div>
  )
}

function formatDate(iso: string): string {
  const locale = toIntlLocale(
    isSupportedLocale(i18n.language) ? i18n.language : 'es',
  )
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
